import { create } from 'zustand'
import { useEffect, useRef, type ReactNode } from 'react'
import { createPortal } from 'react-dom'
import { Icon } from './Icon'

/* In-app confirm dialogs (never the browser's). ask() resolves true for the main button. */
interface Ask {
  title: string
  body?: ReactNode
  ok: string
  cancel?: string | null
  danger?: boolean
  resolve: (v: boolean) => void
}
const useAsk = create<{ cur: Ask | null }>(() => ({ cur: null }))

export function ask(o: Omit<Ask, 'resolve'>): Promise<boolean> {
  return new Promise((resolve) => {
    const prev = useAsk.getState().cur
    prev?.resolve(false)
    useAsk.setState({ cur: { ...o, resolve } })
  })
}

function closeAsk(v: boolean) {
  const cur = useAsk.getState().cur
  if (!cur) return
  useAsk.setState({ cur: null })
  cur.resolve(v)
}

export function AskHost() {
  const cur = useAsk((s) => s.cur)
  const okRef = useRef<HTMLButtonElement>(null)
  useEffect(() => { if (cur) okRef.current?.focus() }, [cur])
  if (!cur) return null
  const close = closeAsk
  return (
    <Scrim onClose={() => close(false)}>
      <div className="popup" role="alertdialog" aria-modal="true" aria-label={cur.title}>
        <h3>{cur.title}</h3>
        {cur.body && <div className="lead">{cur.body}</div>}
        <div className="acts">
          {cur.cancel !== null && <button className="btn btn-ghost" onClick={() => close(false)}>{cur.cancel || 'Cancel'}</button>}
          <button ref={okRef} className={`btn ${cur.danger ? 'btn-danger' : 'btn-primary'}`} onClick={() => close(true)}>{cur.ok}</button>
        </div>
      </div>
    </Scrim>
  )
}

/* The dimmed layer under every popup. It renders on <body>, so no parent's stacking can trap it; Escape and a
   click outside close only the topmost one, and the page behind stops scrolling while it is open. */
const stack: number[] = []
let seq = 0
export function Scrim({ onClose, children, className }: { onClose: () => void; children: ReactNode; className?: string }) {
  const id = useRef(0)
  const close = useRef(onClose)
  close.current = onClose
  useEffect(() => {
    id.current = ++seq
    stack.push(id.current)
    const back = document.activeElement as HTMLElement | null
    const onKey = (e: KeyboardEvent) => { if (e.key === 'Escape' && stack[stack.length - 1] === id.current) { e.stopPropagation(); close.current() } }
    window.addEventListener('keydown', onKey)
    document.body.classList.add('no-scroll')
    return () => {
      window.removeEventListener('keydown', onKey)
      stack.splice(stack.indexOf(id.current), 1)
      if (!stack.length) document.body.classList.remove('no-scroll')
      back?.focus?.()
    }
  }, [])
  return createPortal(
    <div className={'scrim' + (className ? ' ' + className : '')} onMouseDown={(e) => { if (e.target === e.currentTarget) close.current() }}>{children}</div>,
    document.body,
  )
}

/* A popup with any content. */
export function Modal({ onClose, children, label, wide, className }: { onClose: () => void; children: ReactNode; label: string; wide?: boolean; className?: string }) {
  return (
    <Scrim onClose={onClose}>
      <div className={`popup${className ? ' ' + className : ''}`} role="dialog" aria-modal="true" aria-label={label} style={wide ? { width: 'min(640px, 100%)' } : undefined}>
        <button className="x" onClick={onClose} aria-label="Close"><Icon id="i-x" /></button>
        {children}
      </div>
    </Scrim>
  )
}
